import { addMinutes } from 'date-fns';
import { Options, RRule as RRuleBase } from 'rrule';

export class RRule extends RRuleBase {
  static localToNaiveUTC(date: Date) {
    if (!date) {
      return date;
    } // Handle cases where no dtstart is provided
    const timezoneOffset = date.getTimezoneOffset();
    return addMinutes(date, -timezoneOffset);
  }

  static naiveUTCToLocal(date: Date) {
    if (!date) {
      return date;
    }
    const timezoneOffset = date.getTimezoneOffset();
    return addMinutes(date, timezoneOffset);
  }

  // set the dtstart to an actual occurrence of the rrule, to prevent discrepancies between the `between` and `all` methods
  private static alignAndConvertOptions(
    options: Partial<Options>,
  ): Partial<Options> {
    if (!options.dtstart) {
      // If there's no start date, there's nothing to align.
      return options;
    }

    const intendedDtstart = RRule.localToNaiveUTC(options.dtstart);
    // create a temp rrule with the dtstart
    const tempRule = new RRuleBase({
      ...options,
      dtstart: intendedDtstart,
    });

    // get the first occurrence to actually set as dtstart
    const firstOccurrence = tempRule.after(intendedDtstart, true);

    return {
      ...options,
      dtstart: firstOccurrence || intendedDtstart,
    };
  }

  constructor(options: Partial<Options>, noCache?: boolean) {
    const alignedOptions = RRule.alignAndConvertOptions(options);
    super(alignedOptions as Options, noCache);
  }

  // Strip BYSETPOS when paired with a single BYMONTHDAY (no multi-candidate set
  // for BYSETPOS to index into). This combination produces a degenerate rule that
  // never yields occurrences, causing rrule.js to spin for hundreds of ms.
  static sanitize(rruleString: string): string {
    if (!rruleString) {
      return rruleString;
    }

    const base = RRuleBase.fromString(rruleString);
    const opts = base.origOptions;

    if (!!opts.bysetpos && !!opts.bymonthday && !opts.byweekday) {
      const monthDayCount = Array.isArray(opts.bymonthday)
        ? opts.bymonthday.length
        : 1;

      if (monthDayCount <= 1) {
        const { bysetpos: _, wkst: _w, ...rest } = opts;
        return new RRuleBase({
          ...rest,
          dtstart: undefined,
          until: undefined,
          byhour: undefined,
          byminute: undefined,
          bysecond: undefined,
          count: undefined,
        }).toString();
      }
    }

    return rruleString;
  }

  static fromString(rruleString: string) {
    const baseRrule = RRuleBase.fromString(rruleString);
    // remove wkst from the options, as this is not supported in our implementation currently
    const { wkst: _wkst, ...baseOptions } = baseRrule.origOptions;
    return new RRule(baseOptions);
  }

  private preProcess() {
    this.options.until = this.options.until
      ? RRule.localToNaiveUTC(this.options.until)
      : null;
  }
  private postProcess() {
    this.options.until = this.options.until
      ? RRule.naiveUTCToLocal(this.options.until)
      : null;
  }

  after(dt: Date, inc?: boolean): Date | null {
    this.preProcess();
    const naiveUtcDate = RRule.localToNaiveUTC(dt);
    const result = super.after(naiveUtcDate, inc);
    this.postProcess();
    return result
      ? // Convert back to local time
        addMinutes(result, dt.getTimezoneOffset())
      : result;
  }

  between(
    after: Date,
    before: Date,
    inc?: boolean,
    iterator?: (d: Date, len: number) => boolean,
  ): Date[] {
    this.preProcess();
    const naiveUtcStart = RRule.localToNaiveUTC(after);
    const naiveUtcEnd = RRule.localToNaiveUTC(before);
    const results = super.between(naiveUtcStart, naiveUtcEnd, inc, iterator);
    this.postProcess();
    return results.map((result) =>
      addMinutes(result, result.getTimezoneOffset()),
    );
  }

  before(dt: Date, inc?: boolean): Date | null {
    this.preProcess();
    const naiveUtcDate = RRule.localToNaiveUTC(dt);
    const result = super.before(naiveUtcDate, inc);
    this.postProcess();
    return result
      ? // Convert back to local time
        addMinutes(result, result.getTimezoneOffset())
      : result;
  }

  all(iterator?: (d: Date, len: number) => boolean): Date[] {
    this.preProcess();
    const results = super.all(iterator);
    this.postProcess();

    return results.map((result) =>
      addMinutes(result, result.getTimezoneOffset()),
    );
  }

  toString() {
    // remove all unsuported options from the string
    return new RRuleBase({
      ...this.origOptions,
      dtstart: undefined,
      until: undefined,
      byhour: undefined,
      byminute: undefined,
      bysecond: undefined,
      wkst: undefined,
      count: undefined,
    }).toString();
  }
}
